import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useStateContext } from "contexts/ContextProvider";
import { useBrand } from "contexts/BrandContext";
import { useAuth } from "contexts/AuthContext";
import { validateAccount } from "helpers/apiClient";
import { getVerificationCode } from "helpers/verificationCode";
import loadUserProfile from "./loadUserProfile";

const VerifyEmail = ({ token }) => {
  const [message, setMessage] = useState("Verificando tu email...");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setActiveComponent, setUserProfile, userProfile, setIsLoadingUserPermissions } = useStateContext();
  const { handleLogin, setAuthUserId } = useAuth();
  const { brand } = useBrand();
  const attemptedCode = useRef(null);
  const code = token || getVerificationCode(location.search);

  useEffect(() => {
    if (!code) {
      setMessage("");
      setError("No se encontró el código de verificación.");
      return undefined;
    }
    if (attemptedCode.current === code) return undefined;
    attemptedCode.current = code;
    let mounted = true;
    let redirectTimer;
    const verify = async () => {
      setLoading(true);
      setError("");
      try {
        const session = await validateAccount(code);
        const profile = await loadUserProfile(setUserProfile, userProfile, setIsLoadingUserPermissions);
        if (!mounted) return;
        setAuthUserId(profile.userId);
        handleLogin({ ...session, userId: profile.userId });
        setMessage("Cuenta verificada correctamente. Redirigiendo al panel...");
        redirectTimer = setTimeout(() => {
          if (!mounted) return;
          setActiveComponent("dashboard");
          navigate("/", { replace: true });
        }, 1200);
      } catch (requestError) {
        if (mounted) {
          setMessage("No se pudo verificar la cuenta.");
          setError(requestError.message || "La verificación ha fallado. Inténtalo de nuevo.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    verify();
    return () => {
      mounted = false;
      clearTimeout(redirectTimer);
    };
    // The link code determines this one-time action. Including mutable profile
    // context dependencies would repeat verification of this single-use code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="flex w-full flex-col justify-center gap-y-4 items-center">
      <img src={brand.logo} alt={brand.brandName} className="pointer-events-none object-contain h-12" />
      <div className="max-w-md w-full flex flex-col gap-y-4 md:gap-y-8">
        <h2 className="font-medium leading-7 dark:text-white">Verificando tu cuenta...</h2>
        <div className="flex flex-col gap-y-4">
          {message && <p className="text-dark-blue-950 w-fit">{message}</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="h-4 flex justify-center">{loading && <Icon icon="svg-spinners:180-ring" className="text-dark-blue-700" />}</div>
          <span className="text-sm text-center dark:text-white">
            ¿No te redirecciona a la pantalla de inicio?
            <Link className="inline-block text-center ml-1 text-[#1D91E0] hover:text-blue-600" onClick={() => setActiveComponent("login")}>Inicio de sesión</Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
